import Tooltip from "@/components/ui/tooltip";

import { cx } from "@/lib/tailwind-utils";

import { TAG_DEFAULT_COLOR, TagLike } from "./tag-types";

type TagDisplaySize = "sm" | "md" | "lg";

interface TagDisplayProps {
  tags: TagLike[];
  maxVisibleTags?: number;
  tagSize?: TagDisplaySize;
}

// This row has its own swatch markup rather than using TagSwatch: an emoji
// here sits in a fixed box so a mixed row of emoji and dots stays aligned,
// and both variants animate on hover. TagSwatch is the plain inline swatch.
const EMOJI_SIZES: Record<TagDisplaySize, string> = {
  sm: "w-4 h-4 text-caption",
  md: "w-5 h-5 text-body",
  lg: "w-6 h-6 text-body-lg",
};

const DOT_SIZES: Record<TagDisplaySize, string> = {
  sm: "w-3 h-3 text-caption",
  md: "w-4 h-4 text-body",
  lg: "w-5 h-5 text-body-lg",
};

function SingleTagDisplay({
  tag,
  size,
}: {
  tag: TagLike;
  size: TagDisplaySize;
}) {
  if (tag.emoji) {
    return (
      <Tooltip content={tag.name}>
        <span
          className={cx(
            "inline-flex items-center justify-center rounded-full transition-colors hover:bg-muted",
            EMOJI_SIZES[size],
          )}
        >
          {tag.emoji}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={tag.name}>
      <div
        className={cx(
          "rounded-full border-2 border-white shadow-sm transition-transform hover:scale-110",
          DOT_SIZES[size],
        )}
        style={{ backgroundColor: tag.color || TAG_DEFAULT_COLOR }}
      />
    </Tooltip>
  );
}

/**
 * A compact, read-only row of tag swatches with a "+N" overflow tooltip.
 * Inactive patient tags are dropped: retiring a tag hides it without
 * unassigning it. Opportunity tags carry no `isActive`; the API already leaves
 * archived ones out.
 */
export default function TagDisplay({
  tags,
  maxVisibleTags = 3,
  tagSize = "sm",
}: TagDisplayProps) {
  // No archived filter: both catalogs drop archived tags server-side.
  const visibleTags = (tags ?? []).slice(0, maxVisibleTags);
  const hiddenTags = (tags ?? []).slice(maxVisibleTags);
  const hiddenTagsCount = hiddenTags.length;

  if (visibleTags.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-shrink-0 items-center gap-1">
      {visibleTags.map((tag) => (
        <SingleTagDisplay key={tag.id} tag={tag} size={tagSize} />
      ))}
      {hiddenTagsCount > 0 && (
        <Tooltip
          triggerAsChild
          content={
            <ul className="flex flex-col gap-1">
              {hiddenTags.map((tag) => (
                <li key={tag.id} className="flex items-center gap-2">
                  {tag.emoji ? (
                    <span className="inline-flex h-4 w-4 items-center justify-center text-body">
                      {tag.emoji}
                    </span>
                  ) : (
                    <span
                      className="h-3 w-3 flex-shrink-0 rounded-full border border-white/40"
                      style={{
                        backgroundColor: tag.color || TAG_DEFAULT_COLOR,
                      }}
                    />
                  )}
                  <span>{tag.name}</span>
                </li>
              ))}
            </ul>
          }
        >
          <span className="text-caption-strong text-muted-foreground">
            +{hiddenTagsCount}
          </span>
        </Tooltip>
      )}
    </div>
  );
}
